import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography, Container, Breadcrumbs, Link, alpha } from '@mui/material';
import SettingsNavigation from './SettingsNavigation';
import GeneralSettings from './GeneralSettings';
import SecuritySettings from './SecuritySettings';
import NotificationSettings from './NotificationSettings';
import SchoolSettings from './SchoolSettings';
import UserSettings from './UserSettings';
import { Link as RouterLink } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';

const SettingsModule = () => {
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  return (
    <Box sx={{ 
      backgroundColor: safeLineColors.lightGray, 
      minHeight: '100vh',
      pb: 4
    }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          sx={{ 
            mb: 3,
            '& .MuiBreadcrumbs-separator': {
              color: safeLineColors.mutedGray
            }
          }}
        >
          <Link 
            component={RouterLink}
            to="/dashboard"
            underline="hover" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.mediumCharcoal,
              cursor: 'pointer',
              '&:hover': {
                color: safeLineColors.flameOrange
              }
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Dashboard
          </Link>
          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.flameOrange,
              fontWeight: 'medium'
            }}
          >
            <SettingsIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Settings
          </Typography>
        </Breadcrumbs>

        {/* Enhanced Header */}
        <Box sx={{ 
          mb: 3,
          background: `linear-gradient(135deg, ${safeLineColors.pureWhite} 0%, ${safeLineColors.orangeTint} 100%)`,
          borderRadius: 3,
          border: `1px solid ${safeLineColors.coolGray}`,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
          }
        }}>
          <Box sx={{ p: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2
            }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 3,
                background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                mr: 3,
                boxShadow: `0 8px 16px ${alpha(safeLineColors.flameOrange, 0.3)}`
              }}>
                <SettingsIcon sx={{ color: safeLineColors.pureWhite, fontSize: 32 }} />
              </Box>
              <Box>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    color: safeLineColors.darkCharcoal,
                    mb: 1,
                    background: `linear-gradient(135deg, ${safeLineColors.darkCharcoal}, ${safeLineColors.mediumCharcoal})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Settings & Configuration
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: safeLineColors.mediumCharcoal,
                    fontWeight: 400
                  }}
                >
                  Manage your account, security, and system preferences
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Tab navigation */}
        <Box sx={{ mb: 3 }}>
          <SettingsNavigation />
        </Box>

        {/* Main content area */}
        <Box>
          <React.Suspense fallback={
            <Box sx={{ 
              p: 8, 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400
            }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  border: `4px solid ${safeLineColors.orangeTint}`,
                  borderTop: `4px solid ${safeLineColors.flameOrange}`,
                  animation: 'spin 1s linear infinite',
                  mb: 3,
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: safeLineColors.mediumCharcoal,
                  fontWeight: 'medium',
                  mb: 1
                }}
              >
                Loading Settings...
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: safeLineColors.mutedGray }}
              >
                Please wait while we load your preferences.
              </Typography>
            </Box>
          }>
            <Routes>
              <Route path="" element={<Navigate to="general" replace />} />
              <Route path="general" element={<GeneralSettings />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="notifications" element={<NotificationSettings />} />
              <Route path="school" element={<SchoolSettings />} />
              <Route path="user" element={<UserSettings />} />
            </Routes>
          </React.Suspense>
        </Box>
      </Container>
    </Box>
  );
};

export default SettingsModule; 